import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { useCallback, useEffect } from "react";
import {
  ChartDataState,
  ChartStatus,
  setChartStatus,
  setChartData,
} from "../store/chart";
import {
  transformQuizFromBE,
  transformQuizFromMocks,
} from "../utils/transform";
import { CHART_MOCKED_API_RESPONSE } from "../services/mock";
import { ROOT_ID } from "../utils/constants";
import { CmsChartData, CmsProduct, fetchChartData } from "../services/cms";
import { trackChartLoad } from "../analytics";

const PDP_PRODUCT_REGEX = /\/([a-zA-Z]*[0-9].*?)\.html/;

export const useChart = () => {
  const dispatch = useDispatch();
  const chartState = useSelector((state: RootState) => state.chart);

  const getSKUfromPDP = () => {
    let isPDP = false;
    let PDPsku = "";

    try {
      const [, sku] = window.location.pathname.match(
        PDP_PRODUCT_REGEX,
      ) as RegExpMatchArray;
      isPDP = true;
      PDPsku = sku.toLowerCase();
    } catch (_) {
      // not a PDP page
    }

    return {
      isPDP,
      PDPsku,
    };
  };

  const getDataWithPDPProduct = (
    data: CmsChartData,
    PDPsku = "",
  ): CmsChartData => {
    if (!data?.comparison_chart?.products) return data;

    const filteredProducts = data.comparison_chart.products.reduce(
      (acc, prod) => {
        if (
          prod.sku?.toLowerCase() === PDPsku ||
          prod.name?.toLowerCase() === PDPsku
        ) {
          return [
            {
              ...prod,
              desktop_preselected: true,
              mobile_preselected: true,
            },
            ...acc,
          ];
        }

        acc.push(prod);
        return acc;
      },
      [] as CmsProduct[],
    );

    return {
      ...data,
      comparison_chart: {
        ...data.comparison_chart,
        products: filteredProducts,
      },
    };
  };

  const fetchDataFromBE = useCallback(async (): Promise<ChartDataState> => {
    const shouldUseMockedData = process.env.REACT_APP_USE_MOCK === "true";
    if (shouldUseMockedData) {
      return transformQuizFromMocks(CHART_MOCKED_API_RESPONSE);
    }
    const rootElement = document.getElementById(ROOT_ID);
    if (!rootElement) {
      throw new Error(`Root element: ${ROOT_ID} not found`);
    }
    const { apiUrl, experienceId } = rootElement.dataset;
    if (!apiUrl) {
      throw new Error(
        `Root element: ${ROOT_ID} must have data-api-url attribute`,
      );
    }
    if (!experienceId) {
      throw new Error(
        `Root element: ${ROOT_ID} must have data-experience-id attribute`,
      );
    }
    const cmsData = await fetchChartData(apiUrl, experienceId);

    const { isPDP, PDPsku } = getSKUfromPDP();

    if (isPDP) {
      return transformQuizFromBE(getDataWithPDPProduct(cmsData, PDPsku));
    } else {
      return transformQuizFromBE(cmsData);
    }
  }, []);

  useEffect(() => {
    if (chartState.status === ChartStatus.INITIAL) {
      dispatch(setChartStatus(ChartStatus.LOADING));
      fetchDataFromBE().then((data) => {
        dispatch(setChartData(data));
        trackChartLoad();
      });
    }
  }, [chartState.status, dispatch, fetchDataFromBE]);

  return chartState;
};

export default useChart;
